/**
 * shared/api-endpoints.ts
 *
 * Single source of truth for all API base URLs.
 *
 * Rules:
 *   - No browser globals, no imports — this file MUST be importable from Node.js
 *     (Playwright tests) as well as from browser service classes.
 *   - Each service constructor does:  super(API.xxx)
 *   - Each Playwright test imports from here instead of hard-coding strings.
 *
 * When an endpoint path changes → update ONE entry here.  Done.
 */

export const API = {
  // ── Assets ─────────────────────────────────────────────────────────────────
  assets: '/api/assets',

  // ── Messaging / Chat ───────────────────────────────────────────────────────
  messaging: '/api/messaging',

  // ── Campaigns ──────────────────────────────────────────────────────────────
  campaigns: '/api/campaigns',

  // ── Accommodation ──────────────────────────────────────────────────────────
  buildings:  '/api/accommodation/buildings',
  rooms:      '/api/rooms',
  roomTypes:  '/room-types',
  floors:     '/api/floors',
  bookings:   '/bookings',
  requests:   '/requests',
  sleepover:  '/sleepover',
  leases:     '/api/leases',
  apply:      '/apply',
  campusTypes:'/campus-types',

  // ── Dashboard / Charting ───────────────────────────────────────────────────
  charting:  '/api/dashboard/stats/charting',
  dashboard: '/api/dashboard',

  // ── Communications ─────────────────────────────────────────────────────────
  communications: '/api/communications',
  imapSettings:   '/imap-settings',

  // ── HR ─────────────────────────────────────────────────────────────────────
  hr:        '/api/hr',
  employees: '/employees',

  // ── Accounts / Finance ────────────────────────────────────────────────────
  accounts:    '/accounts',
  ageAnalyses: '/age-analyses',

  // ── Students & Finance ─────────────────────────────────────────────────────
  students:              '/students',                           // MVC views + legacy CRUD
  studentsApi:           '/api/students',                       // REST base — sub-resources live here
  studentRooms:          '/api/students/rooms',                 // includes /history, /archive
  studentGuardians:      '/api/students/guardians',
  studentLedger:      '/students/ledger',
  studentAttachments:    '/api/students/attachments',
  studentInvoices:       '/api/student/invoices',
  studentPayments:       '/student-payments',
  bankStatement:         '/api/student-payments/bank-statement',
  invoiceManagement:  '/api/invoice-management',
  invoiceSchedule:    '/invoice-schedule',
  paymentRequisitions:'/payment-requisitions',
  merchants:          '/merchants',

  // ── Procurement ────────────────────────────────────────────────────────────
  purchaseOrders:   '/api/purchase-orders',
  purchaseRequests: '/api/purchase-requests',
  /** Table/MVC controller prefix — NOT the REST API; use sourcingApi for service calls */
  sourcing:         '/sourcing',
  sourcingApi:      '/api/sourcing',
  suppliers:        '/suppliers',
  goodsReceived:    '/goods-received',
  stock:            '/stock',

  // ── Operations ─────────────────────────────────────────────────────────────
  jobCards:    '/api/job-cards',
  maintenance: '/maintenance',
  inventory:   '/inventory',
  pmReports:   '/pm-reports',
  complaints:  '/complaints',
  blacklist:   '/blacklist',
  incidents:   '/api/incidents',
  inspections: '/api/inspections',
  tasks:       '/api/tasks',

  // ── Users & Roles ──────────────────────────────────────────────────────────
  users:       '/api/users',
  roles:       '/api/roles',
  permissions: '/api/permissions',
  notes:       '/api/notes',

  // ── Remittance ─────────────────────────────────────────────────────────────
  remittance:        '/remittance',
  remittanceBuilder: '/remittance/builder',

  // ── System / Settings ──────────────────────────────────────────────────────
  notifications: '/notifications',
  integration:   '/api/integration',
  templating:    '/api/templating',
  calendar:      '/calendar',

  // ── Chart widgets / dashboard (distinct from the charting-builder base) ────
  chartingApi:  '/api/charting',
  inventoryApi: '/api/inventory',
} as const;


